import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import toast from 'react-hot-toast';

export interface Credential {
  id: string;
  name: string;
  issuer: string;
  status: 'VERIFIED' | 'PENDING' | 'EXPIRED';
  expires: string;
  type: string;
}

export interface UserPassport {
  status?: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'UNVERIFIED' | string;
  vettingLevel: number;
  credentials: Credential[];
}

export const usePassport = () => {
  return useQuery<UserPassport>({
    queryKey: ['user-passport'],
    queryFn: () => apiWorker.get('/api/users/me/passport').then(r => {
      const data = (r.data && Array.isArray(r.data.content)) ? r.data.content[0] : (r.data?.content || r.data);
      if (!data) return { vettingLevel: 0, credentials: [], status: 'UNVERIFIED' };
      if (!data.credentials && data.passports) {
        const credentials: Credential[] = Object.entries(data.passports).map(([k]) => ({
          id: k,
          name: k.replace(/_/g, ' '),
          issuer: 'OrgSets Identity',
          status: 'VERIFIED' as const,
          expires: 'Never',
          type: k
        }));
        return {
          ...data,
          vettingLevel: data.vettingLevel ?? 1,
          status: data.status || 'VERIFIED',
          credentials
        };
      }
      return {
        ...data,
        vettingLevel: data.vettingLevel ?? 0,
        status: data.status || 'UNVERIFIED',
        credentials: Array.isArray(data.credentials) ? data.credentials : []
      };
    }),
  });
};

export const useVerifyPassport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: any) => apiWorker.post('/api/users/me/passport/verify', data || {}).then(r => r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-passport'] });
      toast.success(data?.status === 'PENDING' ? 'Verification submitted and is under review.' : 'Passport verified');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to submit passport verification');
    },
  });
};

export const useUpdatePassport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiWorker.patch('/api/users/me/passport', data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-passport'] });
      toast.success('Passport updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update passport');
    },
  });
};

export const useUserPreferences = () => {
  return useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => apiWorker.get('/api/users/me').then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
  });
};

export const useUpdateUserPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiWorker.patch('/api/users/me', data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
      toast.success('Preferences updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update preferences');
    },
  });
};
